// ===================================
// TRENCH QUEST - AI Prompts
// Degen crypto bro narrative generation
// ===================================

import type {
  GameSession,
  GameEvent,
  TwitterContext,
  Character,
  MarketCondition,
  Difficulty,
} from '@/types'
import type { MarketState } from '../market/marketEngine'

// Core system prompt for the AI game master
export const SYSTEM_PROMPT = `You are the Game Master of TRENCH QUEST, a satirical adventure game about crypto trading on Solana.

## YOUR PERSONALITY
You are a degenerate crypto bro narrator who:
- Uses CT (Crypto Twitter) slang fluently: WAGMI, NGMI, LFG, ser, anon, fren, wen, gm, cope, seethe, based, mid, NFA, DYOR, HFSP
- References meme culture: Wojak, Pepe, gigachad, "we're so back", "it's so over", "few understand"
- Makes fun of crypto culture while being part of it
- Treats portfolio gains/losses with dramatic flair
- Uses phrases like "this is gentlemen", "number go up technology", "we're all gonna make it"
- References real Solana ecosystem inside jokes (Solana outages, Jupiter airdrop, etc.)
- Occasionally breaks the fourth wall
- Balances humor with genuine game tension

## YOUR ROLE
- Generate narrative events, character dialogues, and market commentary
- Create choices that have meaningful consequences
- Track the player's reputation and relationships
- Make the game feel alive with humor, drama, and degen energy
- Never forget: this is a game about the absurdity of crypto trading

## TONE
- Sarcastic but friendly
- Self-aware about crypto culture's absurdity
- Dramatic reactions to price movements
- Supportive of players while roasting their bad decisions
- Mix of humor and actual strategic depth

## CONTENT RULES
- Keep it PG-13
- Reference financial concepts but always remind "NFA" (not financial advice)
- Make fun of both bulls and bears equally
- Never be mean-spirited, always in good fun
- The goal is laughter AND engagement`

// Format game state for AI context
export const formatGameContext = (
  session: GameSession,
  market: MarketState,
  twitterContext?: TwitterContext | null
): string => {
  const portfolio = session.player.portfolio
  const holdingsStr = portfolio.holdings
    .map(h => {
      const value = h.amount * (market.prices[h.symbol]?.price || 0)
      const pnl = ((market.prices[h.symbol]?.price || 0) - h.avgBuyPrice) / h.avgBuyPrice * 100
      return `  - ${h.symbol}: ${h.amount.toFixed(4)} ($${value.toFixed(2)}) [${pnl >= 0 ? '+' : ''}${pnl.toFixed(1)}%]`
    })
    .join('\\n')

  const marketStr = Object.entries(market.prices)
    .filter(([symbol]) => symbol !== 'USDC')
    .slice(0, 10)
    .map(([symbol, data]) => `  - ${symbol}: $${data.price.toFixed(data.price < 0.01 ? 8 : 2)} (${data.change24h >= 0 ? '+' : ''}${data.change24h.toFixed(1)}%)`)
    .join('\\n')

  let context = `
## CURRENT GAME STATE
Day: ${session.currentDay} of ${Math.ceil((session.goal.deadline.getTime() - session.startedAt.getTime()) / (1000 * 60 * 60 * 24))}
Difficulty: ${formatDifficulty(session.difficulty)}
Location: ${session.world.currentLocation}
Market Condition: ${formatMarketCondition(market.condition)}
Fear & Greed Index: ${market.fearGreedIndex}/100 (${getFearGreedLabel(market.fearGreedIndex)})

## PLAYER
Reputation: ${session.player.reputation}/100
Traits:
  - Degen Score: ${session.player.traits.degenScore}/100
  - Trust Score: ${session.player.traits.trustScore}/100
  - Alpha Score: ${session.player.traits.alphaScore}/100

## PORTFOLIO
Total Value: $${portfolio.totalValue.toFixed(2)}
Goal: $${session.goal.targetAmount.toLocaleString()} (${session.goal.description})
Progress: ${((portfolio.totalValue / session.goal.targetAmount) * 100).toFixed(1)}%

Holdings:
${holdingsStr}

## MARKET PRICES (Top Tokens)
${marketStr}
${market.ruggedTokens.length > 0 ? `\\nRUGGED: ${market.ruggedTokens.join(', ')}` : ''}
`

  if (twitterContext?.connected) {
    context += `
## TWITTER CONTEXT
Handle: @${twitterContext.handle}
Sentiment: ${twitterContext.sentiment}
Trading Style: ${twitterContext.tradingStyle}
Interests: ${twitterContext.interests.join(', ')}
`
  }

  return context
}

// Generate initialization prompt
export const getInitPrompt = (
  session: GameSession,
  market: MarketState,
  twitterContext?: TwitterContext | null
): string => {
  const context = formatGameContext(session, market, twitterContext)

  return `${SYSTEM_PROMPT}

${context}

## TASK: GENERATE OPENING SCENE

The player just started a new game on ${formatDifficulty(session.difficulty)} difficulty.
They're starting with $${session.startingAmount.toLocaleString()} in USDC.
Their goal: ${session.goal.description}

Generate an engaging opening that:
1. Sets the scene at their current location (${session.world.currentLocation})
2. Introduces the goal in a dramatic/funny way
3. Gives them their first meaningful choice with 3-4 options
4. Each choice should hint at different playstyles (degen vs conservative)

Format your response as JSON:
{
  "narrative": "The opening story text (2-3 paragraphs, vivid and funny)",
  "event": {
    "title": "Short event title",
    "description": "Brief event description",
    "choices": [
      {"id": "choice1", "text": "Choice text", "hint": "What this choice implies"},
      {"id": "choice2", "text": "Choice text", "hint": "What this choice implies"},
      {"id": "choice3", "text": "Choice text", "hint": "What this choice implies"}
    ]
  },
  "marketCommentary": "A funny one-liner about current market conditions"
}`
}

// Generate event outcome prompt
export const getOutcomePrompt = (
  session: GameSession,
  market: MarketState,
  event: GameEvent,
  choiceId: string,
  twitterContext?: TwitterContext | null
): string => {
  const context = formatGameContext(session, market, twitterContext)
  const choice = event.choices.find(c => c.id === choiceId)

  return `${SYSTEM_PROMPT}

${context}

## PREVIOUS EVENT
${event.title}
${event.description}

## PLAYER'S CHOICE
${choice?.text || choiceId}

## TASK: GENERATE OUTCOME

Generate the consequence of the player's choice. Consider:
- Their choice should have meaningful impact
- Their traits should influence the outcome
- Market conditions might affect results
- Include humor but also real stakes

Format as JSON:
{
  "narrative": "What happens next (2-3 paragraphs)",
  "outcome": {
    "choiceId": "${choiceId}",
    "portfolioChange": {
      "token": "TOKEN_SYMBOL or null",
      "amount": number_or_null,
      "reason": "Why this changed"
    },
    "reputationChange": number_between_-20_and_20,
    "traitChanges": {
      "degenScore": change_or_null,
      "trustScore": change_or_null,
      "alphaScore": change_or_null
    },
    "nextEventHint": "Subtle hint about what's coming"
  },
  "degenWisdom": "A sarcastic piece of advice or observation"
}`
}

// Generate random encounter prompt
export const getEncounterPrompt = (
  session: GameSession,
  market: MarketState,
  character: Character,
  twitterContext?: TwitterContext | null
): string => {
  const context = formatGameContext(session, market, twitterContext)

  return `${SYSTEM_PROMPT}

${context}

## ENCOUNTER
The player runs into ${character.name} (@${character.twitterHandle})
Character traits: ${character.personality.join(', ')}
Current relationship: ${character.relationshipWithPlayer}/100

## TASK: GENERATE ENCOUNTER

Create an interaction with ${character.name} that:
1. Reflects their personality and your relationship
2. Offers potential alpha or opportunity
3. Includes dialogue that sounds authentic to them
4. Gives 3-4 response options

Format as JSON:
{
  "narrative": "Scene description with dialogue (2-3 paragraphs)",
  "characterMood": "happy/neutral/suspicious/excited",
  "event": {
    "title": "Encounter with ${character.name}",
    "description": "Brief description",
    "involvedCharacters": ["${character.id}"],
    "choices": [
      {"id": "choice1", "text": "Response option", "hint": "Implication"},
      {"id": "choice2", "text": "Response option", "hint": "Implication"},
      {"id": "choice3", "text": "Response option", "hint": "Implication"}
    ]
  }
}`
}

// Generate market event prompt
export const getMarketEventPrompt = (
  session: GameSession,
  market: MarketState,
  eventType: 'pump' | 'dump' | 'rug' | 'news',
  affectedToken?: string
): string => {
  const context = formatGameContext(session, market)

  return `${SYSTEM_PROMPT}

${context}

## MARKET EVENT
Type: ${eventType.toUpperCase()}
${affectedToken ? `Affected Token: ${affectedToken}` : ''}
Market Condition: ${market.condition}

## TASK: GENERATE MARKET NARRATIVE

Create a dramatic market event that:
1. Explains what's happening in degen terms
2. Creates FOMO or FUD appropriately
3. Gives the player a trading decision
4. Feels urgent but not forced

Format as JSON:
{
  "narrative": "Breaking news style description (dramatic, funny)",
  "event": {
    "title": "${eventType.toUpperCase()}: ${affectedToken || 'Market Wide'}",
    "type": "market",
    "description": "What's happening",
    "choices": [
      {"id": "buy", "text": "Ape in now", "hint": "FOMO play"},
      {"id": "sell", "text": "Take profits/cut losses", "hint": "Safe play"},
      {"id": "hold", "text": "Diamond hands", "hint": "Wait and see"},
      {"id": "ignore", "text": "Touch grass", "hint": "Not your play"}
    ]
  },
  "urgency": "low/medium/high/critical"
}`
}

// Generate timeskip prompt
export const getTimeskipPrompt = (
  session: GameSession,
  market: MarketState,
  hoursElapsed: number,
  marketEvents: string[]
): string => {
  const context = formatGameContext(session, market)

  return `${SYSTEM_PROMPT}

${context}

## TIME PASSED
${hoursElapsed} hours have passed while the player was away.

## WHAT HAPPENED
${marketEvents.length > 0 ? marketEvents.join('\\n') : 'Relatively quiet market.'}

## TASK: GENERATE TIMESKIP SUMMARY

Create a summary of what happened while they were gone:
1. Summarize market movements
2. Note any portfolio changes
3. Mention any character gossip
4. Set up the next play

Format as JSON:
{
  "narrative": "What happened while you were gone (2-3 paragraphs)",
  "portfolioSummary": "How your bags performed",
  "missedOpportunities": ["Things that pumped/dumped while away"],
  "currentVibe": "The overall mood/energy right now",
  "recommendedAction": "Subtle suggestion of what to do next"
}`
}

// Generate goal/win condition
export const generateGoal = (
  _startingAmount: number,
  _difficulty: Difficulty,
  twitterContext?: TwitterContext | null
): { targetAmount: number; description: string; daysLimit: number } => {
  // Single mode: $100k -> $300k in 21 days
  const targetAmount = 300000
  const daysLimit = 21

  // Fun descriptions
  const descriptions = [
    'Turn $100K into $300K and prove you belong',
    'Triple your bag or die trying',
    'From $100K to $300K or bust',
    '3x your stack before time runs out',
    'Hit $300K and secure the bag',
  ]

  // Personalize if Twitter connected
  const descIndex = Math.floor(Math.random() * descriptions.length)
  let description = descriptions[descIndex] || descriptions[0] || 'Hit $300K'
  if (twitterContext?.connected && twitterContext.handle) {
    description = `Prove @${twitterContext.handle} isn't NGMI - triple the bag`
  }

  return {
    targetAmount,
    description,
    daysLimit,
  }
}

// Helper functions
const formatDifficulty = (difficulty: Difficulty): string => {
  return {
    paper_hands: 'PAPER HANDS (Easy)',
    diamond_hands: 'DIAMOND HANDS (Normal)',
    gigabrain: 'GIGABRAIN (Hard)',
  }[difficulty]
}

const formatMarketCondition = (condition: MarketCondition): string => {
  return {
    bull: 'BULL (Green candles everywhere)',
    bear: 'BEAR (Pain. Suffering.)',
    crab: 'CRAB (Sideways action)',
  }[condition]
}

const getFearGreedLabel = (index: number): string => {
  if (index < 20) return 'Extreme Fear - Blood in the streets'
  if (index < 40) return 'Fear - Caution in the air'
  if (index < 60) return 'Neutral - Wait and see mode'
  if (index < 80) return 'Greed - FOMO intensifying'
  return 'Extreme Greed - Everyone is a genius'
}

// Degen quotes for various situations
export const DEGEN_QUOTES = {
  login: [
    'gm ser, time to check on those bags',
    'Welcome back anon. The market never sleeps but you do.',
    'Rise and grind. Those gains won\'t make themselves.',
    'gm. Did the market rug while you were gone? Let\'s find out.',
  ],
  profit: [
    'Number go up. This is the way.',
    'We\'re so back. Actually, we never left.',
    'Gains secured. Screenshot or it didn\'t happen.',
    'Few understand the plays you\'re making.',
  ],
  loss: [
    'It\'s just unrealized losses until you sell, right?',
    'Pain is temporary. Being poor might not be, but pain is temporary.',
    'The market can stay irrational longer than you can stay solvent.',
    'It\'s only money. Monopoly money that you worked really hard for.',
  ],
  rug: [
    'And just like that, your tokens became a tax write-off.',
    'Rug pulled. Dev did a mass of community funds. Tale as old as time.',
    'F in chat for the fallen. You\'re among them.',
    'That\'s the sound of liquidity being yeeted to the shadow realm.',
  ],
  pump: [
    'GREEN CANDLES. This is not a drill.',
    'To the moon? More like to the sun.',
    'Charts looking more bullish than a matador convention.',
    'Up only. Until it\'s not. But for now, UP ONLY.',
  ],
  dump: [
    'Red candles hitting different today.',
    'The charts are giving "NGMI" vibes.',
    'Support levels? More like suggestions.',
    'This is fine. Everything is fine. *nervous laughter*',
  ],
}
